/**
 * City Proximity Mapping
 * Maps major cities to their nearest cities in priority order
 * Used for sorting blogs by location relevance
 */

// Normalize city name for matching
const normalizeCityName = (cityName) => {
  if (!cityName) return '';
  return cityName
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/greater|city|town|nagar|pur/gi, '')
    .trim();
};

// City proximity mapping - nearest cities in priority order
const CITY_PROXIMITY_MAP = {
  // Gujarat
  'surat': ['ahmedabad', 'vadodara', 'mumbai', 'pune', 'bangalore', 'hyderabad', 'delhi'],
  'ahmedabad': ['surat', 'vadodara', 'mumbai', 'pune', 'bangalore', 'delhi', 'hyderabad'],
  'vadodara': ['ahmedabad', 'surat', 'mumbai', 'pune', 'bangalore', 'delhi'],
  'rajkot': ['ahmedabad', 'surat', 'vadodara', 'mumbai', 'pune'],
  'bhavnagar': ['ahmedabad', 'surat', 'vadodara', 'mumbai'],
  
  // Maharashtra
  'mumbai': ['pune', 'ahmedabad', 'surat', 'bangalore', 'hyderabad', 'delhi', 'nashik'],
  'pune': ['mumbai', 'ahmedabad', 'bangalore', 'hyderabad', 'nashik', 'delhi'],
  'nashik': ['mumbai', 'pune', 'ahmedabad', 'delhi'],
  'nagpur': ['hyderabad', 'mumbai', 'pune', 'bangalore', 'delhi'],
  
  // Karnataka
  'bangalore': ['chennai', 'hyderabad', 'mumbai', 'pune', 'coimbatore', 'mysore', 'delhi'],
  'mysore': ['bangalore', 'chennai', 'coimbatore', 'hyderabad'],
  'hubli': ['bangalore', 'mumbai', 'pune', 'goa'],
  'mangalore': ['bangalore', 'cochin', 'mysore', 'goa'],
  
  // Tamil Nadu
  'chennai': ['bangalore', 'coimbatore', 'hyderabad', 'mumbai', 'pune', 'delhi'],
  'coimbatore': ['chennai', 'bangalore', 'mysore', 'kochi'],
  'madurai': ['chennai', 'coimbatore', 'bangalore', 'trivandrum'],
  
  // Telangana/Andhra Pradesh
  'hyderabad': ['bangalore', 'mumbai', 'pune', 'chennai', 'vijayawada', 'delhi'],
  'vijayawada': ['hyderabad', 'chennai', 'bangalore', 'visakhapatnam'],
  'visakhapatnam': ['vijayawada', 'hyderabad', 'chennai', 'bhubaneswar'],
  
  // Delhi NCR
  'delhi': ['gurgaon', 'noida', 'jaipur', 'chandigarh', 'mumbai', 'bangalore', 'ahmedabad'],
  'gurgaon': ['delhi', 'noida', 'jaipur', 'chandigarh'],
  'noida': ['delhi', 'gurgaon', 'jaipur', 'lucknow'],
  
  // Rajasthan
  'jaipur': ['delhi', 'gurgaon', 'ahmedabad', 'mumbai', 'udaipur'],
  'udaipur': ['jaipur', 'ahmedabad', 'mumbai', 'delhi'],
  'jodhpur': ['jaipur', 'ahmedabad', 'udaipur', 'delhi'],
  
  // Uttar Pradesh
  'lucknow': ['delhi', 'kanpur', 'varanasi', 'allahabad', 'noida'],
  'kanpur': ['lucknow', 'delhi', 'allahabad', 'varanasi'],
  'varanasi': ['lucknow', 'allahabad', 'patna', 'delhi'],
  'agra': ['delhi', 'jaipur', 'lucknow', 'gwalior'],
  
  // West Bengal
  'kolkata': ['howrah', 'durgapur', 'siliguri', 'delhi', 'mumbai', 'bangalore'],
  'howrah': ['kolkata', 'durgapur', 'siliguri'],
  'durgapur': ['kolkata', 'howrah', 'siliguri'],
  
  // Kerala
  'kochi': ['coimbatore', 'bangalore', 'trivandrum', 'chennai'],
  'trivandrum': ['kochi', 'chennai', 'coimbatore', 'bangalore'],
  'calicut': ['kochi', 'bangalore', 'mysore', 'coimbatore'],
  
  // Punjab
  'chandigarh': ['delhi', 'gurgaon', 'amritsar', 'ludhiana'],
  'amritsar': ['chandigarh', 'ludhiana', 'delhi', 'jalandhar'],
  'ludhiana': ['chandigarh', 'amritsar', 'delhi'],
  
  // Haryana
  'faridabad': ['delhi', 'gurgaon', 'noida'],
  
  // Madhya Pradesh
  'bhopal': ['indore', 'gwalior', 'delhi', 'mumbai'],
  'indore': ['bhopal', 'mumbai', 'pune', 'ahmedabad'],
  'gwalior': ['delhi', 'agra', 'bhopal', 'jaipur'],
  
  // Odisha
  'bhubaneswar': ['cuttack', 'visakhapatnam', 'kolkata', 'hyderabad'],
  'cuttack': ['bhubaneswar', 'visakhapatnam', 'kolkata'],
  
  // Bihar
  'patna': ['varanasi', 'lucknow', 'kolkata', 'delhi'],
  'gaya': ['patna', 'varanasi', 'kolkata'],
  
  // Jharkhand
  'ranchi': ['patna', 'kolkata', 'bhubaneswar', 'delhi'],
  'jamshedpur': ['kolkata', 'ranchi', 'patna'],
  
  // Uttarakhand
  'dehradun': ['delhi', 'chandigarh', 'haridwar'],
  'haridwar': ['dehradun', 'delhi', 'chandigarh'],
  
  // Assam
  'guwahati': ['silchar', 'kolkata', 'delhi'],
  'silchar': ['guwahati', 'kolkata'],
};

/**
 * Get proximity priority for a city
 * Returns an array of cities in order of proximity (nearest first)
 */
export const getCityProximityList = (cityName) => {
  if (!cityName) return [];
  
  const normalized = normalizeCityName(cityName);
  return CITY_PROXIMITY_MAP[normalized] || [];
};

/**
 * Get priority score for a blog based on user's city
 * Lower score = higher priority
 * 0 = user's city
 * 1, 2, 3... = nearest cities
 * 999 = others
 */
export const getBlogCityPriority = (blog, userCity) => {
  if (!userCity || !blog) return 999;
  
  const userCityNormalized = normalizeCityName(userCity);
  const proximityList = getCityProximityList(userCity);
  
  // Check if blog is about user's city
  const titleLower = (blog?.title || '').toLowerCase();
  const descLower = (blog?.short_description || '').toLowerCase();
  const fullText = `${titleLower} ${descLower}`;
  
  // Check user's city first
  const cityVariations = [
    userCityNormalized,
    userCityNormalized.replace(/\s+/g, '-'),
    userCityNormalized.replace(/\s+/g, ''),
  ];
  
  for (const variation of cityVariations) {
    if (variation.length >= 3 && fullText.includes(variation)) {
      return 0; // User's city - highest priority
    }
  }
  
  // Check nearest cities
  for (let i = 0; i < proximityList.length; i++) {
    const nearbyCity = proximityList[i];
    const nearbyVariations = [
      nearbyCity,
      nearbyCity.replace(/\s+/g, '-'),
      nearbyCity.replace(/\s+/g, ''),
    ];
    
    for (const variation of nearbyVariations) {
      if (variation.length >= 3 && fullText.includes(variation)) {
        return i + 1; // Priority based on proximity order
      }
    }
  }
  
  return 999; // Not related to user's city or nearby cities
};

/**
 * Extract city name from text (blog title/description)
 */
export const extractCityFromText = (text) => {
  if (!text) return null;
  
  const textLower = text.toLowerCase();
  const allCities = Object.keys(CITY_PROXIMITY_MAP);
  
  // Check for city names in the text
  for (const city of allCities) {
    const cityVariations = [
      city,
      city.replace(/\s+/g, '-'),
      city.replace(/\s+/g, ''),
    ];
    
    for (const variation of cityVariations) {
      if (variation.length >= 3 && textLower.includes(variation)) {
        return city;
      }
    }
  }
  
  return null;
};

